import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import { useToast } from "hooks/toast";
import {
  listTransportadoras,
  adicionarTransportadora,
  consultarCnpj,
  revisarApelidoTransportadora,
} from "services/onboardingApi";
import { digitsOnly, formatDocument, isValidCnpj } from "utils/formatDoc";
import { esteiraEstado } from "views/Empresa/Transportadoras/MinhasTransportadoras/esteiraEstado";

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

const PAPEIS = [
  { key: "ti", label: "Tecnologia (API/SFTP)" },
  { key: "cotacao", label: "Comercial (cotação)" },
  { key: "logistica", label: "Operacional (dia a dia)" },
];

const CONTATO_TIPO_LABEL = { 4: "TI", 3: "Cotação", 5: "Logística" };

const FORM_VAZIO = {
  cnpj: "",
  nome: "",
  issRecolhe: "Não",
  issTipoCalculo: "DIVIDIDO",
  vencimentoDias: "30",
  pesoCte: "KG",
  contatos: {
    ti: { nome: "", email: "" },
    cotacao: { nome: "", email: "" },
    logistica: { nome: "", email: "" },
  },
};

// Transportadoras do onboarding (H3 / PO1-2430). Fonte típica: aba "Lista
// CNPJ Transportador" da planilha. Reusa o auto-cadastro (CNPJ + BrasilAPI);
// contatos semeiam o roteamento de e-mail do autopilot (PO1-2015).
// PO1-3284 (épico PO1-3279): cada transportadora passa pela porta única e abre
// o seu Onboarding de Transportadora — o contato de TI (nome + e-mail) é
// obrigatório e cada item mostra o estado da esteira (`estado`).
export default function TransportadorasStep({ idEmbarcador, onChanged }) {
  const { addToast } = useToast();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(FORM_VAZIO);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [razaoConsultada, setRazaoConsultada] = useState("");
  // PO1-2658: revisão de apelido pendente (sugestão do LLM ou texto
  // original suspeito) — idVinculo do item em edição inline.
  const [revisandoId, setRevisandoId] = useState(null);
  const [apelidoRevisao, setApelidoRevisao] = useState("");
  const [salvandoRevisao, setSalvandoRevisao] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const data = await listTransportadoras(idEmbarcador);
      setItems(data.items || []);
    } catch (err) {
      console.error("[TransportadorasStep] list:", err);
    } finally {
      setCarregando(false);
    }
  }, [idEmbarcador]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const setField = useCallback((campo, valor) => {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }, []);

  const setContato = useCallback((papel, campo, valor) => {
    setForm((prev) => ({
      ...prev,
      contatos: {
        ...prev.contatos,
        [papel]: { ...prev.contatos[papel], [campo]: valor },
      },
    }));
  }, []);

  // Ao sair do CNPJ válido, consulta a BrasilAPI só como REFERÊNCIA: mostra a
  // razão social e sugere a fantasia no campo, mas quem cadastra resume o
  // apelido (a Fantasia vira o nome da pasta SFTP no Local Arquivo).
  const handleCnpjBlur = useCallback(async () => {
    if (!isValidCnpj(form.cnpj)) return;
    try {
      const dados = await consultarCnpj(digitsOnly(form.cnpj));
      setRazaoConsultada(dados.razaoSocial || "");
      setForm((prev) =>
        prev.nome.trim()
          ? prev
          : { ...prev, nome: dados.fantasia !== dados.razaoSocial ? dados.fantasia : "" }
      );
    } catch (err) {
      console.error("[TransportadorasStep] consultarCnpj:", err);
    }
  }, [form.cnpj]);

  const handleAdicionar = useCallback(async () => {
    if (!isValidCnpj(form.cnpj)) {
      addToast({
        place: "tr",
        type: "danger",
        title: "CNPJ inválido",
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }
    if (!form.nome.trim()) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Informe o apelido da transportadora",
        description:
          'Use o apelido curto (ex.: "União Transportes") — ele vira o nome da pasta no SFTP.',
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }
    const ti = form.contatos.ti;
    if (!ti.nome.trim() || !EMAIL_RE.test(ti.email.trim())) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Informe o contato de TI",
        description: "Nome e e-mail do contato de TI são obrigatórios: é quem recebe o acesso SFTP e o convite do portal.",
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }
    setSalvando(true);
    try {
      const r = await adicionarTransportadora(idEmbarcador, {
        cnpj: digitsOnly(form.cnpj),
        nome: form.nome,
        issRecolhe: form.issRecolhe === "Sim",
        issTipoCalculo: form.issTipoCalculo,
        vencimentoDias: Number(form.vencimentoDias) || 30,
        pesoCte: form.pesoCte,
        contatos: form.contatos,
      });
      const estado = r && r.estado;
      const emCuradoria = estado === "EM_CURADORIA";
      const badge = estado ? esteiraEstado({ fase: estado }) : null;
      addToast({
        place: "tr",
        type: emCuradoria ? "warning" : "success",
        title: emCuradoria ? "Transportadora aguardando a Curadoria" : "Transportadora vinculada",
        description: emCuradoria
          ? (r.curadoria && r.curadoria.motivo) || "A matriz 0001 precisa de ajuste. Libere em Transportadoras › Curadoria."
          : badge
          ? `Esteira: ${badge.rotulo}.`
          : undefined,
        icon: emCuradoria ? "tim-icons icon-alert-circle-exc" : "tim-icons icon-check-2",
      });
      setForm(FORM_VAZIO);
      setRazaoConsultada("");
      setMostrarForm(false);
      await carregar();
      onChanged();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao vincular transportadora",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSalvando(false);
    }
  }, [form, idEmbarcador, addToast, carregar, onChanged]);

  // PO1-2658: confirma/edita o apelido pendente e libera o passo Local
  // Arquivo para esta transportadora.
  const handleRevisarApelido = useCallback(
    async (item) => {
      const novoApelido = apelidoRevisao.trim();
      if (!novoApelido) {
        addToast({
          place: "tr",
          type: "danger",
          title: "Informe o apelido",
          icon: "tim-icons icon-alert-circle-exc",
        });
        return;
      }
      setSalvandoRevisao(true);
      try {
        await revisarApelidoTransportadora(
          idEmbarcador,
          item.idTransportadora,
          novoApelido
        );
        addToast({
          place: "tr",
          type: "success",
          title: "Apelido revisado",
          icon: "tim-icons icon-check-2",
        });
        setRevisandoId(null);
        await carregar();
        onChanged();
      } catch (err) {
        const msg =
          (err.response && err.response.data && err.response.data.error) ||
          err.message;
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro ao revisar apelido",
          description: msg,
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setSalvandoRevisao(false);
      }
    },
    [apelidoRevisao, idEmbarcador, addToast, carregar, onChanged]
  );

  const paramValor = (item, nome) => {
    const p = (item.parametros || []).find((x) => x.nome === nome);
    return p ? p.valor : null;
  };

  // Estado da esteira do vínculo (PO1-3284): o backend devolve `estado` (ou
  // `esteira.fase`); sem nada, é vínculo anterior à jornada e não inventa fase.
  const badgeEstado = (item) => {
    const fase = item.estado || (item.esteira && item.esteira.fase) || null;
    if (!fase) return null;
    const e = esteiraEstado({ fase, motivoDevolucao: item.motivoDevolucao || item.motivo });
    return (
      <span className={`onb-badge ${e.badge}`} title={e.ajuda}>
        {e.rotulo}
      </span>
    );
  };

  if (carregando) return <Spinner size="sm" />;

  return (
    <div>
      {items.map((item) => (
        <div className="onb-filial" key={item.idVinculo} style={{ flexWrap: "wrap" }}>
          <div>
            <div className="nm">{item.fantasia || item.razaoSocial}</div>
            <div className="ci">
              {formatDocument(item.cnpjCpf)} ·{" "}
              {(item.contatos || [])
                .map((c) => CONTATO_TIPO_LABEL[c.idTipoContato] || "?")
                .join(", ") || "sem contatos"}
            </div>
          </div>
          <div className="d-flex" style={{ gap: 6 }}>
            {badgeEstado(item)}
            {item.apelidoPendenteRevisao && (
              <>
                <span className="onb-badge pend" title="Apelido parece razão social: confira quando puder. Não trava o onboarding.">Apelido a revisar</span>
                <Button
                  color="link"
                  size="sm"
                  className="p-0"
                  onClick={() => {
                    setRevisandoId(item.idVinculo);
                    setApelidoRevisao(item.fantasia || "");
                  }}
                >
                  Revisar
                </Button>
              </>
            )}
            <span
              className={`onb-badge ${
                paramValor(item, "ISSRecolhe") === "Sim" ? "done" : "lock"
              }`}
            >
              ISS {paramValor(item, "ISSRecolhe") === "Sim" ? "Sim" : "Não"}
            </span>
            <span className="onb-badge auto">
              venc {paramValor(item, "FaturaQtdDiasPrevisaoVencimento") || "30"}d
            </span>
            <span className="onb-badge auto">
              {paramValor(item, "NotaFiscalTipoPesoCalculoImp") || "KG"}
            </span>
          </div>
          {revisandoId === item.idVinculo && (
            <div className="d-flex" style={{ gap: 6, width: "100%", marginTop: 8 }}>
              <Input
                bsSize="sm"
                value={apelidoRevisao}
                onChange={(e) => setApelidoRevisao(e.target.value)}
                placeholder='Apelido curto (ex.: "União Transportes")'
              />
              <Button
                color="primary"
                size="sm"
                disabled={salvandoRevisao}
                onClick={() => handleRevisarApelido(item)}
              >
                {salvandoRevisao ? <Spinner size="sm" /> : "Confirmar"}
              </Button>
              <Button
                color="secondary"
                size="sm"
                outline
                onClick={() => setRevisandoId(null)}
              >
                Cancelar
              </Button>
            </div>
          )}
        </div>
      ))}

      {!mostrarForm && (
        <Button color="link" size="sm" onClick={() => setMostrarForm(true)}>
          + adicionar transportadora
        </Button>
      )}

      {mostrarForm && (
        <div style={{ marginTop: 10 }}>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label>CNPJ</Label>
                <Input
                  value={formatDocument(form.cnpj)}
                  onChange={(e) => setField("cnpj", e.target.value)}
                  onBlur={handleCnpjBlur}
                  placeholder="00.000.000/0000-00"
                />
              </FormGroup>
            </Col>
            <Col md="8">
              <FormGroup>
                <Label>Apelido (nome curto, obrigatório)</Label>
                <Input
                  value={form.nome}
                  onChange={(e) => setField("nome", e.target.value)}
                  placeholder='ex.: "União Transportes"'
                />
                {razaoConsultada && (
                  <small style={{ color: "#9a9a9a" }}>
                    Razão social: {razaoConsultada} — resuma no apelido acima
                    (vira o nome da pasta no SFTP)
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="3">
              <FormGroup>
                <Label>ISS recolhe?</Label>
                <Input
                  type="select"
                  value={form.issRecolhe}
                  onChange={(e) => setField("issRecolhe", e.target.value)}
                >
                  <option>Não</option>
                  <option>Sim</option>
                </Input>
              </FormGroup>
            </Col>
            {form.issRecolhe === "Sim" && (
              <Col md="3">
                <FormGroup>
                  <Label>Cálculo do ISS</Label>
                  <Input
                    type="select"
                    value={form.issTipoCalculo}
                    onChange={(e) => setField("issTipoCalculo", e.target.value)}
                  >
                    <option>DIVIDIDO</option>
                    <option>MULTIPLICADO</option>
                  </Input>
                </FormGroup>
              </Col>
            )}
            <Col md="3">
              <FormGroup>
                <Label>Vencimento (dias)</Label>
                <Input
                  value={form.vencimentoDias}
                  onChange={(e) => setField("vencimentoDias", e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label>Peso p/ CT-e</Label>
                <Input
                  type="select"
                  value={form.pesoCte}
                  onChange={(e) => setField("pesoCte", e.target.value)}
                >
                  <option value="KG">KG</option>
                  <option value="CUBADO">Cubado</option>
                  <option value="MAIOR">Comparativo (maior)</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Label style={{ fontWeight: 600, fontSize: 13 }}>
            Contatos (3 papéis) — TI obrigatório
          </Label>
          {PAPEIS.map((papel) => (
            <Row key={papel.key}>
              <Col md="4" className="d-flex align-items-center">
                <small>
                  {papel.label}
                  {papel.key === "ti" ? " *" : ""}
                </small>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Input
                    value={form.contatos[papel.key].nome}
                    onChange={(e) => setContato(papel.key, "nome", e.target.value)}
                    placeholder="Nome"
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Input
                    value={form.contatos[papel.key].email}
                    onChange={(e) =>
                      setContato(papel.key, "email", e.target.value)
                    }
                    placeholder="email@transportadora.com"
                  />
                </FormGroup>
              </Col>
            </Row>
          ))}

          <div className="d-flex justify-content-end" style={{ gap: 10 }}>
            <Button color="secondary" onClick={() => setMostrarForm(false)}>
              Cancelar
            </Button>
            <Button color="primary" onClick={handleAdicionar} disabled={salvando}>
              {salvando ? <Spinner size="sm" /> : "Vincular transportadora"}
            </Button>
          </div>
        </div>
      )}

      <div className="onb-info">
        <span role="img" aria-label="informação">ℹ️</span>
        <span>
          <b>ISS recolhe / vencimento</b> são perguntas com default sugerido —
          só divergências são gravadas. Peso do CT-e grava &quot;KG&quot;
          visível por padrão. O contato de TI é obrigatório: recebe o acesso
          SFTP e o convite do portal; os outros semeiam o roteamento de e-mail
          do Autopilot. Cada transportadora entra na esteira (homologação da
          Everlog se for nova na Rede; depois pastas, usuário SFTP e portal).
        </span>
      </div>
    </div>
  );
}
